import React from "react";
import {Button, CircularProgress, Tooltip} from "@mui/material";
import {Autorenew, Block, DoDisturbOutlined, PublishOutlined} from "@mui/icons-material";

import {Blue3, Grey17} from "../../theme/styles";

// The moves available from the state a record is in, as buttons.
//
// A PORT OF cres-t5/src/components/Common/LifecycleActions, deliberately identical. Foundation
// objects and Data Catalog records share one revision lifecycle -- DRAFT, RELEASED, DEPRECATED and
// the frozen states -- and people move between the two apps. Revise meaning the same thing but
// LOOKING different in each was the whole problem: t1 drew every action as a filled blue button,
// including Void, so "end this permanently" and "start a draft" were the same shout.
//
// Outlined, with the destructive ones in red, and the SOURCE_ACTIONS table left behind because a
// Source is a t5 object. Keep the two files in step; they are the same component twice because the
// apps do not share a package, not because they are meant to differ.
//
// Which moves exist is decided HERE only for display. The server refuses an illegal transition
// whatever this offers -- Update Source answers 409, and the revision services refuse a Release that
// is not from a DRAFT. This is convenience, not enforcement, and the two agreeing is a nicety.
//
// A record whose state offers nothing renders nothing: an archived source and a voided revision are
// terminal, and a disabled button implying otherwise would be a worse answer than no button.
//
// WHILE ONE IS RUNNING, ALL OF THEM STOP. A transition is a round trip to the server, and until it
// answers the buttons still describe the old status -- so a second click sends the same transition
// again and the server, correctly, refuses it: "Only a DRAFT revision may be released". The caller
// did nothing wrong and gets an error for it. The one that was clicked shows a spinner in place of
// its icon so it is visible WHICH move is underway, not merely that something is.

export type LifecycleAction = {
    label: string;
    action: string;
    hint: string;
    icon: React.ReactNode;
    destructive?: boolean;
};

// Terms and Rules share a revision lifecycle exactly, so they share this table.
export const REVISION_ACTIONS: Record<string, LifecycleAction[]> = {
    DRAFT: [
        {label: "Release", action: "Release", icon: <PublishOutlined sx={{fontSize: 18}}/>,
         hint: "Make this revision the agreed one"},
        // DRAFT ONLY, because advancing renumbers the record -- doing that to something already
        // released would change what a reader was told they were looking at. The page removes this
        // when the record is already at the last stage; the server refuses it either way.
        {label: "Advance stage", action: "AdvanceStage", icon: <Autorenew sx={{fontSize: 18}}/>,
         hint: "Move to the next revision stage. The revision is renumbered from the start of it"},
        {label: "Cancel", action: "Cancel", icon: <DoDisturbOutlined sx={{fontSize: 18}}/>,
         hint: "Abandon this draft and free its revision number", destructive: true},
        // Foundation allows a draft to be voided outright as well as cancelled; t5's revisions do
        // not, which is the one place the two tables differ.
        {label: "Void", action: "Void", icon: <Block sx={{fontSize: 18}}/>,
         hint: "End it outright, without releasing it first. This cannot be undone",
         destructive: true},
    ],
    RELEASED: [
        {label: "Revise", action: "Revise", icon: <Autorenew sx={{fontSize: 18}}/>,
         hint: "Start a new draft from this content"},
        {label: "Deprecate", action: "Deprecate", icon: <Block sx={{fontSize: 18}}/>,
         hint: "Signal it is on the way out. It keeps resolving for anything already using it"},
    ],
    // Deprecated is LIVE, so the only move is to end it. It cannot be revised: the backend's
    // Revise requires RELEASED.
    DEPRECATED: [
        {label: "Void", action: "Void", icon: <Block sx={{fontSize: 18}}/>,
         hint: "End it. This cannot be undone", destructive: true},
    ],
    SUPERSEDED: [],
    CANCELLED: [],
    VOID: [],
};

export default function LifecycleActions(props: {
    status?: string | null;
    actions: Record<string, LifecycleAction[]>;
    onAction: (action: string) => void;
    disabled?: boolean;
    /** The action currently in flight, if any. Its button spins; every button is disabled. */
    busyAction?: string | null;
}) {
    const {status, actions, onAction, disabled, busyAction} = props;

    const available = actions[status ?? ""] ?? [];

    return (
        <>
            {available.map(action => (
                <Tooltip key={action.action + action.label}
                         title={busyAction ? `${busyAction} in progress…` : action.hint}>
                    <span>
                        <Button variant="outlined" size="small"
                                disabled={disabled || !!busyAction}
                                startIcon={busyAction === action.action
                                    ? <CircularProgress size={14} thickness={5} sx={{color: "inherit"}}/>
                                    : action.icon}
                                onClick={() => onAction(action.action)}
                                sx={{textTransform: "none", fontSize: 13,
                                     borderColor: "#A8A9AF",
                                     color: action.destructive ? "#C0392B" : Grey17,
                                     "&:hover": {
                                         borderColor: action.destructive ? "#C0392B" : Blue3,
                                         color: action.destructive ? "#C0392B" : Blue3,
                                         backgroundColor: "transparent",
                                     }}}>
                            {action.label}
                        </Button>
                    </span>
                </Tooltip>
            ))}
        </>
    );
}
